import * as Yup from "yup";

/**
 * Field types supported by the GenericStepForm
 */
export type FieldType = "radio" | "textarea" | "text" | "select" | "checkbox";

/**
 * Radio option configuration
 */
export interface RadioOption {
  value: string;
  label: string;
  icon?: string;
}

/**
 * Select option configuration
 */
export interface SelectOption {
  value: string;
  label: string;
}

/**
 * Form field configuration
 */
export interface FormFieldConfig {
  name: string;
  type: FieldType;
  label: string;
  placeholder?: string;
  helpText?: string;
  exampleText?: string;
  options?: RadioOption[];
  selectOptions?: SelectOption[];
  rows?: number;
  validation?: Yup.Schema;
}

/**
 * Section configuration for grouping fields
 */
export interface SectionConfig {
  title: string;
  description?: string;
  fields: FormFieldConfig[];
}

/**
 * Complete step configuration
 */
export interface StepConfig {
  stepNumber: number;
  title: string;
  description: string;
  sections: SectionConfig[];
}

// Common validation schemas
const textValidation = Yup.string().min(1, "Minimum 1 character").max(1000, "Maximum 1000 characters");
const textAreaValidation = Yup.string().min(1, "Minimum 1 character").max(5000, "Maximum 5000 characters");

/**
 * Step 1: Market Segmentation configuration
 */
export const step1Config: StepConfig = {
  stepNumber: 1,
  title: "Market Segmentation",
  description: "After going through the steps in Instructions, fill in your conclusions here — they will be displayed in the dashboard canvas and also in exported data.",
  sections: [
    {
      title: "Market Sides",
      fields: [
        {
          name: "field1",
          type: "radio",
          label: "Select your market type",
          options: [
            { value: "Single Sided", label: "Single Sided", icon: "/media/icons/duotone/Files/Media.svg" },
            { value: "Two-Sided", label: "Two-sided", icon: "/media/icons/duotone/Devices/LTE2.svg" },
            { value: "Multi-Sided", label: "Multi-sided", icon: "/media/icons/duotone/Shopping/Cart3.svg" },
          ],
          validation: Yup.string().oneOf(["Single Sided", "Two-Sided", "Multi-Sided"], "Selecting the field is required"),
        },
      ],
    },
    {
      title: "Market Type",
      fields: [
        {
          name: "field2",
          type: "radio",
          label: "Select B2B or B2C",
          options: [
            { value: "B2C", label: "B2C", icon: "/media/icons/duotone/Files/Media.svg" },
            { value: "B2B", label: "B2B", icon: "/media/icons/duotone/Devices/LTE2.svg" },
            { value: "Both", label: "Both", icon: "/media/icons/duotone/Shopping/Cart3.svg" },
          ],
          validation: Yup.string().oneOf(["B2C", "B2B", "Both"], "Selecting the field is required"),
        },
      ],
    },
    {
      title: "Possible Customer Segments",
      fields: [
        {
          name: "field3",
          type: "textarea",
          label: "Customer Segments",
          placeholder: "Type your market segments here...",
          helpText: "List 5–10 market segments you could pursue, mentioning 2-3 demographic criteria and behaviors.",
          exampleText: "Eg: Young professionals (25-35) in Western Europe, heavy smartphone users, who are unsatisfied with the conservative and limited features of traditional banks, and want instant, fast and flexible access to all their money, credit cards or investments.",
          rows: 7,
          validation: textAreaValidation,
        },
      ],
    },
  ],
};

/**
 * Step 2: Beachhead Market Summary
 */
export const step2Config: StepConfig = {
  stepNumber: 2,
  title: "Select a Beachhead Market",
  description: "After going through the steps in Instructions, fill in your conclusions here — they will be displayed in the dashboard canvas and also in exported data.",
  sections: [
    {
      title: "Beachhead market segment",
      description: "Enter a name for the segment you chose (you will add more detailed description later on).",
      fields: [
        {
          name: "field1",
          type: "text",
          label: "Segment Name",
          placeholder: "Enter segment name...",
          validation: textValidation,
        },
      ],
    },
    {
      title: "Describe the Beachhead Market",
      description: "Describe the beachhead market in detail, explaining how it meets the above criteria and why you think it is the right beachhead market.",
      fields: [
        {
          name: "field2",
          type: "textarea",
          label: "Description",
          placeholder: "Type your market segments here...",
          rows: 7,
          validation: textAreaValidation,
        },
      ],
    },
  ],
};

/**
 * Step 3: Define end user profile Summary
 */
export const step3Config: StepConfig = {
  stepNumber: 3,
  title: "Build an End User Profile",
  description: "After going through the steps in Instructions, fill in your conclusions here — they will be displayed in the dashboard canvas and also in exported data.",
  sections: [
    {
      title: "End-user roles",
      description: "Beyond actually using your product or service, the end user may have other roles in the organization or household, which are essential in understanding his motivations and behavior.",
      fields: [
        {
          name: "field1",
          type: "radio",
          label: "Champion: he wants the customer to purchase the product.",
          options: [
            { value: "Yes", label: "Yes", icon: "/media/icons/duotone/Files/Media.svg" },
            { value: "No", label: "No", icon: "/media/icons/duotone/Devices/LTE2.svg" },
          ],
          validation: Yup.string().oneOf(["Yes", "No"], "Selecting the field is required"),
        },
        {
          name: "field2",
          type: "radio",
          label: "Primary economic buyer: he has the authority to spend the money.",
          options: [
            { value: "Yes", label: "Yes", icon: "/media/icons/duotone/Files/Media.svg" },
            { value: "No", label: "No", icon: "/media/icons/duotone/Devices/LTE2.svg" },
          ],
          validation: Yup.string().oneOf(["Yes", "No"], "Selecting the field is required"),
        },
        {
          name: "field3",
          type: "radio",
          label: "Influencer/Veto power: he can influence or control the decisions of the Primary Economic Buyer.",
          options: [
            { value: "Yes", label: "Yes", icon: "/media/icons/duotone/Files/Media.svg" },
            { value: "No", label: "No", icon: "/media/icons/duotone/Devices/LTE2.svg" },
          ],
          validation: Yup.string().oneOf(["Yes", "No"], "Selecting the field is required"),
        },
      ],
    },
    {
      title: "Does your Founding Team Include Someone in the end-user profile?",
      description: "This can be a huge advantage, because you will be able to understand in-depth all the criteria above—which can be critical for your success.",
      fields: [
        {
          name: "field4",
          type: "radio",
          label: "Founding team member in profile",
          options: [
            { value: "No", label: "No", icon: "/media/icons/duotone/Files/Media.svg" },
            { value: "Yes1", label: "Yes, one person", icon: "/media/icons/duotone/Devices/LTE2.svg" },
            { value: "Yes2", label: "Yes, more than one person", icon: "/media/icons/duotone/Shopping/Cart3.svg" },
          ],
          validation: Yup.string().oneOf(["No", "Yes1", "Yes2"], "Selecting the field is required"),
        },
      ],
    },
    {
      title: "Clear end-user profile",
      description: "Describe in 3–4 paragraphs the end-user you will be focusing on. Include demographics (gender, age, occupation, income, education, geographic area), but also lifestyle, behavior and socio-graphics.",
      fields: [
        {
          name: "field5",
          type: "textarea",
          label: "End-user profile description",
          placeholder: "Type your market segments here...",
          rows: 7,
          validation: textAreaValidation,
        },
      ],
    },
  ],
};

/**
 * Step 4: Beachhead Market TAM Summary
 */
export const step4Config: StepConfig = {
  stepNumber: 4,
  title: "Calculate TAM for Beachhead",
  description: "After going through the steps in Instructions, fill in your conclusions here — they will be displayed in the dashboard canvas and also in exported data.",
  sections: [
    {
      title: "TAM size",
      description: "What is the calculated size of the TAM for your beachhead market? Keep in mind that for a TAM less than $5 million per year might be too small, while a TAM larger than $100 million might mean that you need to further segment your beachhead market, to be able to better focus on it in the beginning.",
      fields: [
        {
          name: "field1",
          type: "text",
          label: "US$ growing",
          placeholder: "Enter TAM size...",
          validation: textValidation,
        },
        {
          name: "field2",
          type: "text",
          label: "% per year",
          placeholder: "Enter growth rate...",
          validation: textValidation,
        },
        {
          name: "field3",
          type: "textarea",
          label: "Write more details about how you came up with this calculation",
          placeholder: "Type your market segments here...",
          rows: 7,
          validation: textAreaValidation,
        },
      ],
    },
  ],
};

/**
 * Step 5: Persona Summary
 */
export const step5Config: StepConfig = {
  stepNumber: 5,
  title: "Profile the Persona",
  description: "After going through the steps in Instructions, fill in your conclusions here — they will be displayed in the dashboard canvas and also in exported data.",
  sections: [
    {
      title: "Describe customer persona",
      description: "Describe the essentials of the persona below, trying to describe all its characteristics. You can also include a link to a Dropbox (or other) file with the final persona.",
      fields: [
        {
          name: "field1",
          type: "textarea",
          label: "Customer persona",
          placeholder: "Type your market segments here...",
          rows: 7,
          validation: textAreaValidation,
        },
      ],
    },
  ],
};

/**
 * Step 6: Full Life Cycle Use Case Summary
 */
export const step6Config: StepConfig = {
  stepNumber: 6,
  title: "Full Life Cycle Use Case",
  description: "After going through the steps in Instructions, fill in your conclusions here — they will be displayed in the dashboard canvas and also in exported data.",
  sections: [
    {
      title: "Describe the full life cycle use case",
      description: "Map out the complete end-to-end use case for your customer — from discovering their problem through to achieving their goal with your product. Include every step the end user takes.",
      fields: [
        {
          name: "field1",
          type: "textarea",
          label: "Full life cycle use case",
          placeholder: "Describe the customer's journey from problem to resolution...",
          rows: 7,
          validation: textAreaValidation,
        },
      ],
    },
  ],
};

/**
 * Step 7: High-level product specifications Summary
 */
export const step7Config: StepConfig = {
  stepNumber: 7,
  title: "High-Level Product Specification",
  description: "After going through the steps in Instructions, fill in your conclusions here — they will be displayed in the dashboard canvas and also in exported data.",
  sections: [
    {
      title: "Add description of high-level product specifications",
      description: "Paste the image or the link to the high-level product specifications.",
      fields: [
        {
          name: "field1",
          type: "textarea",
          label: "Product specifications",
          placeholder: "Type your market segments here...",
          rows: 7,
          validation: textAreaValidation,
        },
      ],
    },
  ],
};

/**
 * Step 8: Quantify value proposition Summary
 */
export const step8Config: StepConfig = {
  stepNumber: 8,
  title: "Quantify the Value Proposition",
  description: "After going through the steps in Instructions, fill in your conclusions here — they will be displayed in the dashboard canvas and also in exported data.",
  sections: [
    {
      title: "State the Quantified Value Proposition in one sentence",
      fields: [
        {
          name: "field1",
          type: "textarea",
          label: "Value proposition",
          placeholder: "Type your market segments here...",
          rows: 7,
          validation: textAreaValidation,
        },
      ],
    },
  ],
};

/**
 * Step 9: Next 10 customers Summary
 */
export const step9Config: StepConfig = {
  stepNumber: 9,
  title: "Identify Next 10 Customers",
  description: "After going through the steps in Instructions, fill in your conclusions here — they will be displayed in the dashboard canvas and also in exported data.",
  sections: [
    {
      title: "Write down your findings",
      description: "Identifying and interviewing your next 10 customers now ensures that your Persona description and other assumptions hold true for an array of customers. If you have completed this step properly, and made modifications to the other steps from what you learned here, you should have greater confidence moving forward to build the plan for your new venture. Note here the major findings from this step (Persona modifications, invalidated assumptions and other insights).",
      fields: [
        {
          name: "field1",
          type: "textarea",
          label: "Findings",
          placeholder: "Type your market segments here...",
          rows: 7,
          validation: textAreaValidation,
        },
      ],
    },
  ],
};

/**
 * Step 10: Core definition Summary
 */
export const step10Config: StepConfig = {
  stepNumber: 10,
  title: "Define Your Core",
  description: "After going through the steps in Instructions, fill in your conclusions here — they will be displayed in the dashboard canvas and also in exported data.",
  sections: [
    {
      title: "Select the core",
      description: "Choose only one core—this will be the family jewels that you will protect beyond all else. There may be other traps along the way for your potential competitors (which is a good thing if they do not distract you too much) but they are just moats around the family jewels. It may be intellectual property, but there are many other examples of a core that your new venture could develop.",
      fields: [
        {
          name: "field1",
          type: "select",
          label: "Core Type",
          selectOptions: [
            { value: "None", label: "No core defined yet" },
            { value: "Network Effect", label: "Network Effect" },
            { value: "Customer Service", label: "Customer Service" },
            { value: "Lowest Cost", label: "Lowest Cost" },
            { value: "UX", label: "User Experience (UX)" },
            { value: "IP", label: "Intellectual Property" },
            { value: "Supplier Lock", label: "Supplier Lock" },
            { value: "Other", label: "Other Core" },
          ],
          validation: textValidation,
        },
      ],
    },
    {
      title: "Add more details",
      description: "The Core is what you have that your competitors do not. You will protect it over time, and above all else. You will continually work over time to develop and enhance it. Once you agree on a Core, it should not change without a great deal of thought; instead, you should continually make your Core stronger. If it changes often, this is a bad sign because it means you are likely not building it effectively. Describe the Core below:",
      fields: [
        {
          name: "field2",
          type: "textarea",
          label: "Core description",
          placeholder: "Type your market segments here...",
          rows: 7,
          validation: textAreaValidation,
        },
      ],
    },
  ],
};

/**
 * Step 11: Competitive position chart Summary
 */
export const step11Config: StepConfig = {
  stepNumber: 11,
  title: "Chart Competitive Position",
  description: "After going through the steps in Instructions, fill in your conclusions here — they will be displayed in the dashboard canvas and also in exported data.",
  sections: [
    {
      title: "Add description of the chart",
      description: "Paste the image or the link to your competitive position chart.",
      fields: [
        {
          name: "field1",
          type: "textarea",
          label: "Chart description",
          placeholder: "Type your market segments here...",
          rows: 7,
          validation: textAreaValidation,
        },
      ],
    },
  ],
};

/**
 * Step 12: Decision Making Unit Summary
 */
export const step12Config: StepConfig = {
  stepNumber: 12,
  title: "Determine Customer's DMU",
  description: "After going through the steps in Instructions, fill in your conclusions here — they will be displayed in the dashboard canvas and also in exported data.",
  sections: [
    {
      title: "Add description of the DMU map",
      description: "Paste the image or the link to your DMU map.",
      fields: [
        {
          name: "field1",
          type: "textarea",
          label: "DMU description",
          placeholder: "Type your market segments here...",
          rows: 7,
          validation: textAreaValidation,
        },
      ],
    },
  ],
};

/**
 * Step 13: Customer acquisition process Summary
 */
export const step13Config: StepConfig = {
  stepNumber: 13,
  title: "Map Process to Acquire Customer",
  description: "After going through the steps in Instructions, fill in your conclusions here — they will be displayed in the dashboard canvas and also in exported data.",
  sections: [
    {
      title: "Add description of the customer acquisition process map or diagram",
      description: "Mapping out this process is important because you will need to navigate the same process over and over to sell to more customers; so understanding this process will pay dividends later, when you can more easily acquire new customers. Paste the image or the link to your map or diagram.",
      fields: [
        {
          name: "field1",
          type: "textarea",
          label: "Process description",
          placeholder: "Type your market segments here...",
          rows: 7,
          validation: textAreaValidation,
        },
      ],
    },
  ],
};

/**
 * Step 14: TAM Summary
 */
export const step14Config: StepConfig = {
  stepNumber: 14,
  title: "Calculate TAM for Follow-on",
  description: "After going through the steps in Instructions, fill in your conclusions here — they will be displayed in the dashboard canvas and also in exported data.",
  sections: [
    {
      title: "Choosing the follow-on strategy",
      description: "Which strategy will you choose to expand your business?",
      fields: [
        {
          name: "field1",
          type: "radio",
          label: "Follow-on strategy",
          options: [
            { value: "Upselling", label: "Upselling", icon: "/media/icons/duotone/Files/Media.svg" },
            { value: "Adjacent", label: "Adjacent markets", icon: "/media/icons/duotone/Devices/LTE2.svg" },
            { value: "Both", label: "Both strategies", icon: "/media/icons/duotone/Shopping/Cart3.svg" },
          ],
          validation: Yup.string().oneOf(["Upselling", "Adjacent", "Both"], "Selecting the field is required"),
        },
      ],
    },
    {
      title: "Describe the follow-on market",
      description: "List the follow-on markets, their TAM and CAGR (each market on a separate line):",
      fields: [
        {
          name: "field2",
          type: "textarea",
          label: "Follow-on markets",
          placeholder: "Type your market segments here...",
          rows: 7,
          validation: textAreaValidation,
        },
      ],
    },
  ],
};

/**
 * Step 15: Business Model Summary
 */
export const step15Config: StepConfig = {
  stepNumber: 15,
  title: "Design a Business Model",
  description: "After going through the steps in Instructions, fill in your conclusions here — they will be displayed in the dashboard canvas and also in exported data.",
  sections: [
    {
      title: "Choose your main type of business model",
      fields: [
        {
          name: "field1",
          type: "select",
          label: "Business model",
          selectOptions: [
            { value: "One-time up-front charge plus maintenance", label: "One-time up-front charge plus maintenance" },
            { value: "Cost plus", label: "Cost plus" },
            { value: "Hourly Rates", label: "Hourly Rates" },
            { value: "Subscription or leasing", label: "Subscription or leasing" },
            { value: "Licensing", label: "Licensing" },
            { value: "Consumables", label: "Consumables" },
            { value: "Upsell with high-margin products", label: "Upsell with high-margin products" },
            { value: "Advertising", label: "Advertising" },
            { value: "Reselling the collected data", label: "Reselling the collected data" },
            { value: "Transaction fee", label: "Transaction fee" },
            { value: "Usage-based", label: "Usage-based" },
            { value: "Cell phone plan", label: "Cell phone plan" },
            { value: "Parking meter or penalty charges", label: "Parking meter or penalty charges" },
            { value: "Microtransactions", label: "Microtransactions" },
            { value: "Shared savings", label: "Shared savings" },
            { value: "Franchise", label: "Franchise" },
            { value: "Operating and maintenance", label: "Operating and maintenance" },
            { value: "Other", label: "Other" },
          ],
          validation: textValidation,
        },
      ],
    },
    {
      title: "Describe your business model",
      description: "Summarize your business model in a few paragraphs, including details about customers, value creation and capturing, competition and distribution.",
      fields: [
        {
          name: "field2",
          type: "textarea",
          label: "Business model description",
          placeholder: "Type your market segments here...",
          rows: 7,
          validation: textAreaValidation,
        },
      ],
    },
  ],
};

/**
 * Step 16: Pricing Framework Summary
 */
export const step16Config: StepConfig = {
  stepNumber: 16,
  title: "Set Your Pricing Framework",
  description: "After going through the steps in Instructions, fill in your conclusions here — they will be displayed in the dashboard canvas and also in exported data.",
  sections: [
    {
      title: "Describe your pricing framework",
      description: "Summarize your pricing strategy and framework.",
      fields: [
        {
          name: "field1",
          type: "textarea",
          label: "Pricing framework",
          placeholder: "Type your pricing framework here...",
          rows: 7,
          validation: textAreaValidation,
        },
      ],
    },
  ],
};

/**
 * Step 17: LTV calculation Summary
 */
export const step17Config: StepConfig = {
  stepNumber: 17,
  title: "Calculate LTV",
  description: "After going through the steps in Instructions, fill in your conclusions here — they will be displayed in the dashboard canvas and also in exported data.",
  sections: [
    {
      title: "Calculate Lifetime Value",
      fields: [
        {
          name: "field1",
          type: "text",
          label: "One-time Revenue",
          placeholder: "Enter one-time revenue...",
          validation: textValidation,
        },
        {
          name: "field2",
          type: "text",
          label: "Recurring Revenue",
          placeholder: "Enter recurring revenue...",
          validation: textValidation,
        },
        {
          name: "field3",
          type: "text",
          label: "Gross Margin",
          placeholder: "Enter gross margin...",
          validation: textValidation,
        },
        {
          name: "field4",
          type: "text",
          label: "Lifetime",
          placeholder: "Enter customer lifetime...",
          validation: textValidation,
        },
        {
          name: "field5",
          type: "textarea",
          label: "Additional notes",
          placeholder: "Type additional notes here...",
          rows: 5,
          validation: textAreaValidation,
        },
      ],
    },
  ],
};

/**
 * Step 18: Sales process Summary
 */
export const step18Config: StepConfig = {
  stepNumber: 18,
  title: "Map the Sales Process",
  description: "After going through the steps in Instructions, fill in your conclusions here — they will be displayed in the dashboard canvas and also in exported data.",
  sections: [
    {
      title: "Map your sales process",
      description: "Describe your sales funnel and process stages.",
      fields: [
        {
          name: "field1",
          type: "textarea",
          label: "Sales process",
          placeholder: "Type your sales process here...",
          rows: 7,
          validation: textAreaValidation,
        },
        {
          name: "field2",
          type: "text",
          label: "COCA estimate",
          placeholder: "Enter COCA estimate...",
          validation: textValidation,
        },
      ],
    },
  ],
};

/**
 * Step 19: COCA calculation Summary
 */
export const step19Config: StepConfig = {
  stepNumber: 19,
  title: "Calculate COCA",
  description: "After going through the steps in Instructions, fill in your conclusions here — they will be displayed in the dashboard canvas and also in exported data.",
  sections: [
    {
      title: "Calculate Cost of Customer Acquisition",
      fields: [
        {
          name: "field1",
          type: "text",
          label: "Marketing Costs",
          placeholder: "Enter marketing costs...",
          validation: textValidation,
        },
        {
          name: "field2",
          type: "text",
          label: "Sales Costs",
          placeholder: "Enter sales costs...",
          validation: textValidation,
        },
        {
          name: "field3",
          type: "text",
          label: "Total COCA",
          placeholder: "Enter total COCA...",
          validation: textValidation,
        },
        {
          name: "field4",
          type: "textarea",
          label: "Additional notes",
          placeholder: "Type additional notes here...",
          rows: 5,
          validation: textAreaValidation,
        },
      ],
    },
  ],
};

/**
 * Step 20: Identify Key Assumptions Summary
 */
export const step20Config: StepConfig = {
  stepNumber: 20,
  title: "Identify Key Assumptions",
  description: "After going through the steps in Instructions, fill in your conclusions here — they will be displayed in the dashboard canvas and also in exported data.",
  sections: [
    {
      title: "List your key assumptions",
      description: "List the top 5 assumptions that need to be validated.",
      fields: [
        {
          name: "field1",
          type: "text",
          label: "Assumption 1",
          placeholder: "Enter assumption 1...",
          validation: textValidation,
        },
        {
          name: "field2",
          type: "text",
          label: "Assumption 2",
          placeholder: "Enter assumption 2...",
          validation: textValidation,
        },
        {
          name: "field3",
          type: "text",
          label: "Assumption 3",
          placeholder: "Enter assumption 3...",
          validation: textValidation,
        },
        {
          name: "field4",
          type: "text",
          label: "Assumption 4",
          placeholder: "Enter assumption 4...",
          validation: textValidation,
        },
        {
          name: "field5",
          type: "text",
          label: "Assumption 5",
          placeholder: "Enter assumption 5...",
          validation: textValidation,
        },
      ],
    },
  ],
};

/**
 * Step 21: Test Key Assumptions
 */
export const step21Config: StepConfig = {
  stepNumber: 21,
  title: "Test Key Assumptions",
  description: "After going through the steps in Instructions, fill in your conclusions here — they will be displayed in the dashboard canvas and also in exported data.",
  sections: [
    {
      title: "Enter your prioritized list of assumptions",
      description: "Prioritize the list based on the potential impact on the business and its costs.",
      fields: [
        {
          name: "field1",
          type: "text",
          label: "Assumption 1 notes",
          placeholder: "Enter notes...",
          validation: textValidation,
        },
        {
          name: "field2",
          type: "select",
          label: "Outcome",
          selectOptions: [
            { value: "Validated", label: "Validated" },
            { value: "Invalidated", label: "Invalidated" },
            { value: "Pending", label: "Pending" },
          ],
          validation: textValidation,
        },
        {
          name: "field3",
          type: "text",
          label: "Assumption 2 notes",
          placeholder: "Enter notes...",
          validation: textValidation,
        },
        {
          name: "field4",
          type: "select",
          label: "Outcome",
          selectOptions: [
            { value: "Validated", label: "Validated" },
            { value: "Invalidated", label: "Invalidated" },
            { value: "Pending", label: "Pending" },
          ],
          validation: textValidation,
        },
        {
          name: "field5",
          type: "text",
          label: "Assumption 3 notes",
          placeholder: "Enter notes...",
          validation: textValidation,
        },
        {
          name: "field6",
          type: "select",
          label: "Outcome",
          selectOptions: [
            { value: "Validated", label: "Validated" },
            { value: "Invalidated", label: "Invalidated" },
            { value: "Pending", label: "Pending" },
          ],
          validation: textValidation,
        },
        {
          name: "field7",
          type: "text",
          label: "Assumption 4 notes",
          placeholder: "Enter notes...",
          validation: textValidation,
        },
        {
          name: "field8",
          type: "select",
          label: "Outcome",
          selectOptions: [
            { value: "Validated", label: "Validated" },
            { value: "Invalidated", label: "Invalidated" },
            { value: "Pending", label: "Pending" },
          ],
          validation: textValidation,
        },
        {
          name: "field9",
          type: "text",
          label: "Assumption 5 notes",
          placeholder: "Enter notes...",
          validation: textValidation,
        },
        {
          name: "field10",
          type: "select",
          label: "Outcome",
          selectOptions: [
            { value: "Validated", label: "Validated" },
            { value: "Invalidated", label: "Invalidated" },
            { value: "Pending", label: "Pending" },
          ],
          validation: textValidation,
        },
      ],
    },
  ],
};

/**
 * Step 22: MVP Summary
 */
export const step22Config: StepConfig = {
  stepNumber: 22,
  title: "Define the MVP",
  description: "After going through the steps in Instructions, fill in your conclusions here — they will be displayed in the dashboard canvas and also in exported data.",
  sections: [
    {
      title: "Define your Minimum Viable Product",
      description: "Describe your MVP and what it will test.",
      fields: [
        {
          name: "field1",
          type: "textarea",
          label: "MVP description",
          placeholder: "Type your MVP description here...",
          rows: 7,
          validation: textAreaValidation,
        },
      ],
    },
  ],
};

/**
 * Step 23: MVP test results Summary
 */
export const step23Config: StepConfig = {
  stepNumber: 23,
  title: "Show Dogs Will Eat the Food",
  description: "After going through the steps in Instructions, fill in your conclusions here — they will be displayed in the dashboard canvas and also in exported data.",
  sections: [
    {
      title: "Document your MVP test results",
      description: "Summarize the results of your MVP tests.",
      fields: [
        {
          name: "field1",
          type: "textarea",
          label: "Test results",
          placeholder: "Type your test results here...",
          rows: 7,
          validation: textAreaValidation,
        },
      ],
    },
  ],
};

/**
 * Step 24: Product plan Summary
 */
export const step24Config: StepConfig = {
  stepNumber: 24,
  title: "Develop a Product Plan",
  description: "After going through the steps in Instructions, fill in your conclusions here — they will be displayed in the dashboard canvas and also in exported data.",
  sections: [
    {
      title: "Outline your product plan",
      description: "Describe your product roadmap and development plan.",
      fields: [
        {
          name: "field1",
          type: "textarea",
          label: "Product plan",
          placeholder: "Type your product plan here...",
          rows: 7,
          validation: textAreaValidation,
        },
      ],
    },
  ],
};

/**
 * All step configurations
 */
export const stepConfigs: Record<number, StepConfig> = {
  1: step1Config,
  2: step2Config,
  3: step3Config,
  4: step4Config,
  5: step5Config,
  6: step6Config,
  7: step7Config,
  8: step8Config,
  9: step9Config,
  10: step10Config,
  11: step11Config,
  12: step12Config,
  13: step13Config,
  14: step14Config,
  15: step15Config,
  16: step16Config,
  17: step17Config,
  18: step18Config,
  19: step19Config,
  20: step20Config,
  21: step21Config,
  22: step22Config,
  23: step23Config,
  24: step24Config,
};

/**
 * Get configuration for a specific step
 */
export function getStepConfig(stepNumber: number): StepConfig | undefined {
  return stepConfigs[stepNumber];
}

/**
 * Build Yup validation schema from step configuration
 */
export function buildValidationSchema(config: StepConfig): Yup.ObjectSchema<Record<string, unknown>> {
  const shape: Record<string, Yup.Schema> = {};

  config.sections.forEach((section) => {
    section.fields.forEach((field) => {
      if (field.validation) {
        shape[field.name] = field.validation;
      }
    });
  });

  return Yup.object().shape(shape);
}

/**
 * Get initial values from step configuration
 */
export function getInitialValues(config: StepConfig): Record<string, string> {
  const values: Record<string, string> = {};

  config.sections.forEach((section) => {
    section.fields.forEach((field) => {
      values[field.name] = "";
    });
  });

  return values;
}
